import React, { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Clock, Download, Edit, Film, Loader2, Play, X } from "lucide-react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { Link, useNavigate } from "react-router-dom";
import { MyClipQuery, MyClipQueryVariables } from "@/generated/graphql";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useClipPolling } from "@/hooks/use-polling";
import { useToast } from "@/hooks/use-toast";
import { useShareClip } from "@/hooks/use-share-clip";
import { useClipExtraActions } from "@/hooks/use-clip-extra-actions";
import { getClipStatusText } from "@/lib/get-clip-status-text";
import { formatDateTime, formatDuration } from "@/lib/format";
import { downloadClipVideo } from "@/lib/downloads/clip-download";
import { GET_MY_CLIP, RATE_CLIP } from "@/queries/queries";
import ClipTitlesDialog from "@/components/ClipTitlesDialog";
import ClipVersionBadge, { VersionBadgeClip } from "@/components/ClipVersionBadge";
import type { VersionGroup } from "@/lib/clips/groupClipVersions";
import { ClipPostMenu } from "@/components/publishing/ClipPostMenu";
import { useClipPostActions } from "@/hooks/use-clip-post-actions";
import { ClipCardMoreMenu, type ClipCardMenuAction, type ClipCardRating } from "@/components/clips/ClipCardMoreMenu";
import { ClipCoverDialog } from "@/components/clips/cover/ClipCoverDialog";
import {
  canSetClipCover,
  resolveClipCardCoverUrl,
  type ClipCoverOverride,
} from "@/lib/cover/clipCardCover";
import { buildClipCardMenu, resolveClipCardSecondaryAction } from "@/lib/clips/clipCardActions";
import { resolveClipCardPoster, resolveClipPreviewOrientation } from "@/lib/clips/clipCardPoster";
import { aiStudioPathFor } from "@/lib/ai-assistant/aiStudioPaths";
import { clipStudioPath } from "@/lib/freecut-studio/studioPath";
import { canMakeQuoteCarousel } from "@/lib/quoteCarousel/canMakeQuoteCarousel";
import { PublishStatusOverlay } from "@/components/publishing/PublishStatusOverlay";
import type { PublishMediaKinds, PublishUiStatus } from "@/lib/publishing/types";
import { useDownloadUpsell } from "@/contexts/downloadUpsellContext";
import { cn } from "@/lib/utils";

// Utility function to format ETA seconds
const formatEta = (etaSeconds: number | null | undefined, t: (key: string, options?: any) => string): string => {
  if (!etaSeconds || etaSeconds <= 0) return "";

  const minutes = Math.floor(etaSeconds / 60);
  const seconds = Math.round(etaSeconds % 60);

  if (minutes > 0 && seconds > 0) {
    return `${t('clipCard.estimate')} ${minutes}m ${seconds}s`;
  } else if (minutes > 0) {
    return `${t('clipCard.estimate')} ${minutes}m`;
  } else {
    return `${t('clipCard.estimate')} ${seconds}s`;
  }
};

// The card is a size container: the second button drops its text below
// 240px of card width and its chevron below 270px (2 per row on phones,
// 4–6 per row on desktop). Every button keeps its aria-label.
const SECONDARY_LABEL_CLASS = "hidden [@container(min-width:240px)]:inline";
// Below 200px (two cards across a 360–390px phone) Baixar is its icon too.
const DOWNLOAD_LABEL_CLASS = "hidden [@container(min-width:200px)]:inline";
const SECONDARY_CHEVRON_CLASS = "hidden [@container(min-width:270px)]:block";

export interface ClipCardProps {
  clip: MyClipQuery['myClip'];
  versionGroup?: VersionGroup<VersionBadgeClip>;
  onSendToQueue?: (clipId: string) => void;
  onSendToTikTok?: (clipId: string) => void;
  onPublishToYouTube?: (clipId: string) => void;
  onPublishToInstagram?: (clipId: string) => void;
  onPublishToFacebook?: (clipId: string) => void;
  tiktokPublishStatus?: PublishUiStatus;
  tiktokPublishFailReason?: string | null;
  youtubePublishStatus?: PublishUiStatus;
  youtubePublishFailReason?: string | null;
  instagramPublishStatus?: PublishUiStatus;
  instagramPublishFailReason?: string | null;
  facebookPublishStatus?: PublishUiStatus;
  facebookPublishFailReason?: string | null;
  /** Latest publish media_kind per platform ('carousel' adds an icon). */
  publishMediaKinds?: PublishMediaKinds;
  /** Offer "Editar com IA" (the page knows whether the viewer has it). */
  showEditWithAi?: boolean;
  /** Whether the viewer has PRO features (gates the AI cover). */
  hasProFeatures?: boolean;
}

const PENDING_STATUSES = ['processing', 'pre-queued', 'queued'];

/** A clip on /clips: a 9:16 poster, its title and date, then Baixar, Postar
 *  (or Editar) and a "⋯" menu with every other action. */
export const ClipCard: React.FC<ClipCardProps> = ({
  clip,
  versionGroup,
  onSendToQueue,
  onSendToTikTok,
  onPublishToYouTube,
  onPublishToInstagram,
  onPublishToFacebook,
  tiktokPublishStatus,
  tiktokPublishFailReason,
  youtubePublishStatus,
  youtubePublishFailReason,
  instagramPublishStatus,
  instagramPublishFailReason,
  facebookPublishStatus,
  facebookPublishFailReason,
  publishMediaKinds,
  showEditWithAi = false,
  hasProFeatures = false,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { notifyClipDownloaded } = useDownloadUpsell();
  const [rateClipMutation] = useMutation(RATE_CLIP);
  const navigate = useNavigate();
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [titlesDialogOpen, setTitlesDialogOpen] = useState(false);
  const [videoPreviewOpen, setVideoPreviewOpen] = useState(false);
  const [warningModalOpen, setWarningModalOpen] = useState(false);
  const [downloadErrorOpen, setDownloadErrorOpen] = useState(false);
  const [clipRating, setClipRating] = useState<string>(clip.curation_rating || 'neutral');

  // Cover: this session's saved cover wins over the fetched one
  const [coverOverride, setCoverOverride] = useState<ClipCoverOverride>(null);
  const [coverDialogOpen, setCoverDialogOpen] = useState(false);

  // Poster images that failed to load, the fallback video's failure, and the
  // poster's natural size (picks the preview dialog's shape).
  const [failedPosterUrls, setFailedPosterUrls] = useState<ReadonlySet<string>>(() => new Set());
  const [posterVideoFailed, setPosterVideoFailed] = useState(false);
  const [posterSize, setPosterSize] = useState<{ width: number; height: number } | null>(null);

  const markPosterFailed = useCallback((url: string) => {
    setFailedPosterUrls((prev) => new Set(prev).add(url));
  }, []);

  // Lazy query to get fresh clip data only when the user opens the titles dialog
  const [fetchFreshClip, { data: freshClipData }] = useLazyQuery<MyClipQuery, MyClipQueryVariables>(
    GET_MY_CLIP,
    {
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    }
  );

  // Poll while the clip is processing
  const { data: polledClip } = useClipPolling(
    clip.id,
    {
      enabled: (
        PENDING_STATUSES.includes(clip.clipping_status) ||
        PENDING_STATUSES.includes(clip.preparing_status) ||
        PENDING_STATUSES.includes(clip.rendering_status)
      ),
      interval: 5000,
    }
  );

  const currentClip = polledClip || clip;

  const currentStatusState = useMemo(() => {
    if (currentClip.rendering_status !== 'skipped') {
      return currentClip.rendering_status
    }
    if (currentClip.preparing_status !== 'skipped') {
      return currentClip.preparing_status
    }
    return currentClip.clipping_status
  }, [currentClip.clipping_status, currentClip.preparing_status, currentClip.rendering_status]);

  const effectiveStatusLabel = getClipStatusText(currentClip as any);

  const effectiveDownloadUrl = currentClip.finished_video_url
    || currentClip.compressed_video_url
    || currentClip.clipped_video_url;
  const displayName = (currentClip as any)?.name || clip.name;
  const title = currentClip.name || t('clipCard.noName');

  const etaSeconds = polledClip?.eta_seconds || clip.eta_seconds;
  const etaText = formatEta(etaSeconds, t);

  const isExpired = useMemo(() => {
    return (
      currentClip.clipping_status === 'expired' ||
      currentClip.preparing_status === 'expired' ||
      currentClip.rendering_status === 'expired'
    );
  }, [currentClip.clipping_status, currentClip.preparing_status, currentClip.rendering_status]);

  const aiStudioUrl = useMemo(() => {
    if (clip.is_library_clip && clip.library_id) {
      return aiStudioPathFor({ kind: 'library', id: clip.library_id, clipId: clip.id });
    }
    if (!clip.is_library_clip && clip.project_id) {
      return aiStudioPathFor({ kind: 'project', id: clip.project_id, clipId: clip.id });
    }
    return null;
  }, [clip.id, clip.is_library_clip, clip.project_id, clip.library_id]);

  // Project clips go to the project editor, library clips to the library editor
  const editUrl = useMemo(() => {
    if (clip.is_library_clip && clip.library_id) {
      return `/library/${clip.library_id}/edit?clip=${clip.id}`;
    }
    if (!clip.is_library_clip && clip.project_id) {
      return `/project/${clip.project_id}/edit?clip=${clip.id}`;
    }
    return null;
  }, [clip.id, clip.is_library_clip, clip.project_id, clip.library_id]);

  const isCurrentStateSuccess = currentStatusState === 'success'
  const isCurrentStateFailure = currentStatusState === 'failure'
  const isCurrentStateExpired = currentStatusState === 'expired'
  const isCurrentStateProcessing = !isCurrentStateSuccess && !isCurrentStateFailure && !isCurrentStateExpired
  // A cut or prepared clip that was never rendered says so; a rendered one says nothing.
  const showReadyChip = isCurrentStateSuccess && currentClip.rendering_status !== 'success' && !isExpired;

  const coverUrl = resolveClipCardCoverUrl({
    fetchedUrl: currentClip.cover_image_url,
    override: coverOverride,
  });
  const posterUrl = resolveClipCardPoster({
    coverUrl,
    finishedThumbnailUrl: currentClip.finished_video_thumbnail_url,
    thumbnailUrl: currentClip.thumbnail_url,
    failedUrls: failedPosterUrls,
  });
  const previewOrientation = resolveClipPreviewOrientation(posterSize);
  const canPlay = !!effectiveDownloadUrl && !isExpired && !isCurrentStateProcessing && !isCurrentStateFailure;

  const showCoverAction = canSetClipCover({
    canSetCover: currentClip.can_set_cover,
    finishedVideoUrl: currentClip.finished_video_url,
    isRendered: isCurrentStateSuccess,
    isExpired,
  });
  const canShowPublishActions = isCurrentStateSuccess && !!effectiveDownloadUrl && !isExpired;

  const post = useClipPostActions({
    clipId: clip.id,
    canPublish: canShowPublishActions,
    onSendToQueue,
    onSendToTikTok,
    onPublishToYouTube,
    onPublishToInstagram,
    onPublishToFacebook,
    tiktokPublishStatus,
    tiktokPublishFailReason,
    youtubePublishStatus,
    youtubePublishFailReason,
    instagramPublishStatus,
    instagramPublishFailReason,
    facebookPublishStatus,
    facebookPublishFailReason,
  });
  const extras = useClipExtraActions({ canMakeCarousel: canMakeQuoteCarousel(currentClip) });
  const canEdit = !!editUrl && !isExpired;
  const secondary = resolveClipCardSecondaryAction({
    canPost: post.showPostControls && post.actions.length > 0,
    canEdit,
  });
  const menuGroups = buildClipCardMenu({
    secondary,
    canEdit,
    canEditWithAi: showEditWithAi && !!aiStudioUrl && !isExpired,
    canOpenStudio: extras.showStudio,
    canMakeCarousel: extras.showCarousel,
    canSetCover: showCoverAction,
    canGenerateTitles: !isExpired,
    canShare: !!effectiveDownloadUrl && !isExpired,
    canRate: isCurrentStateSuccess,
  });

  const { share, dialog: shareDialog } = useShareClip({
    downloadUrl: effectiveDownloadUrl ?? '',
    clipName: displayName || t('clipCard.noName'),
  });

  const handleDownload = async () => {
    if (!effectiveDownloadUrl || isDownloading) return;

    // A clip that was never rendered downloads as the raw cut: warn first
    if (currentClip.preparing_status === 'success' && currentClip.rendering_status === 'skipped') {
      setWarningModalOpen(true);
      return;
    }

    performDownload();
  };

  const performDownload = async () => {
    if (!effectiveDownloadUrl || isDownloading) return;

    setIsDownloading(true);
    setDownloadProgress(0);

    try {
      await downloadClipVideo({
        clipId: currentClip.id,
        fallbackName: displayName || "clip",
        downloadUrl: effectiveDownloadUrl,
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setDownloadProgress(percentage);
          }
        },
      });
      notifyClipDownloaded();
    } catch (error) {
      console.error('Download failed:', error);
      setDownloadErrorOpen(true);
    } finally {
      setIsDownloading(false);
      setDownloadProgress(0);
    }
  };

  const handleRating = async (rating: ClipCardRating) => {
    try {
      const newRating = clipRating === rating ? 'neutral' : rating;
      await rateClipMutation({ variables: { id: clip.id, rating: newRating } });
      setClipRating(newRating);
      toast({
        title: newRating === 'neutral' ? t('projectEditor.clipRatingRemoved') : t('projectEditor.clipRated'),
      });
    } catch (error) {
      console.error('Error rating clip:', error);
      toast({ title: t('projectEditor.clipRatingError'), variant: "destructive" });
    }
  };

  const handleMenuAction = (action: ClipCardMenuAction) => {
    switch (action) {
      case 'edit':
        if (editUrl) navigate(editUrl);
        return;
      case 'editWithAi':
        if (aiStudioUrl) navigate(aiStudioUrl);
        return;
      case 'studio':
        navigate(clipStudioPath(clip.id));
        return;
      case 'carousel':
        navigate(`/clips/${clip.id}/carousel`);
        return;
      case 'cover':
        setCoverDialogOpen(true);
        return;
      case 'titles':
        // Fetch fresh clip data to ensure we have the latest copy
        fetchFreshClip({ variables: { id: clip.id } });
        setTitlesDialogOpen(true);
        return;
      case 'share':
        void share();
        return;
    }
  };

  const renderStill = () => {
    if (posterUrl) {
      return (
        <img
          key={posterUrl}
          data-testid="clip-card-poster"
          src={posterUrl}
          alt=""
          loading="lazy"
          className={cn("h-full w-full object-contain", isExpired && "opacity-60 grayscale")}
          onLoad={(event) => setPosterSize({
            width: event.currentTarget.naturalWidth,
            height: event.currentTarget.naturalHeight,
          })}
          onError={() => markPosterFailed(posterUrl)}
        />
      );
    }
    // No image at all: show the video's first frame (#t= makes iOS draw it).
    if (currentClip.finished_video_url && !posterVideoFailed) {
      return (
        <video
          data-testid="clip-card-poster-video"
          src={`${currentClip.finished_video_url}#t=0.1`}
          preload="metadata"
          muted
          playsInline
          className={cn("h-full w-full object-contain", isExpired && "opacity-60 grayscale")}
          onError={() => setPosterVideoFailed(true)}
        />
      );
    }
    return (
      <div
        data-testid="clip-card-no-preview"
        className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground"
      >
        <Film className="h-8 w-8" aria-hidden="true" />
      </div>
    );
  };

  return (
    <Card
      data-testid="clip-card"
      className="flex min-w-0 flex-col overflow-hidden [container-type:inline-size]"
    >
      <div className="relative aspect-[9/16] w-full overflow-hidden bg-black">
        {renderStill()}

        {canPlay && (
          <button
            type="button"
            data-testid="clip-card-play"
            aria-label={t('clipCard.play', { name: title })}
            className="group/play absolute inset-0 z-[5] flex items-center justify-center focus-visible:outline-none"
            onClick={() => setVideoPreviewOpen(true)}
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-full border border-white/20 bg-black/50 shadow-[0_2px_16px_rgba(0,0,0,0.4)] backdrop-blur-sm transition-all duration-200 group-hover/play:scale-110 group-hover/play:bg-primary/85 group-focus-visible/play:ring-2 group-focus-visible/play:ring-primary">
              <Play className="ml-0.5 h-5 w-5 fill-white text-white group-hover/play:fill-black group-hover/play:text-black" />
            </span>
          </button>
        )}

        <span
          data-testid="clip-card-duration"
          className="pointer-events-none absolute right-2 top-2 z-10 rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-medium tracking-wide text-white backdrop-blur-sm"
        >
          {formatDuration(currentClip.duration_seconds ?? clip.duration_seconds)}
        </span>

        {!isExpired && (
          <PublishStatusOverlay
            className="absolute left-2 top-2 z-20"
            statuses={[
              {
                platform: 'tiktok',
                status: tiktokPublishStatus,
                failReason: tiktokPublishFailReason,
                mediaKind: publishMediaKinds?.tiktok,
              },
              {
                platform: 'instagram',
                status: instagramPublishStatus,
                failReason: instagramPublishFailReason,
                mediaKind: publishMediaKinds?.instagram,
              },
              {
                platform: 'facebook',
                status: facebookPublishStatus,
                failReason: facebookPublishFailReason,
                mediaKind: publishMediaKinds?.facebook,
              },
              {
                platform: 'youtube',
                status: youtubePublishStatus,
                failReason: youtubePublishFailReason,
                mediaKind: publishMediaKinds?.youtube,
              },
            ]}
          />
        )}

        {(isCurrentStateProcessing || isCurrentStateFailure || isExpired) && (
          <div
            data-testid="clip-card-status"
            className={cn(
              "absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-black/60 p-3 text-center text-xs",
              isCurrentStateFailure && !isExpired && "text-red-400",
              isExpired && "text-amber-400",
              isCurrentStateProcessing && !isExpired && "text-white",
            )}
          >
            {isExpired ? (
              <Clock className="h-7 w-7" aria-hidden="true" />
            ) : isCurrentStateFailure ? (
              <X className="h-7 w-7" aria-hidden="true" />
            ) : (
              <Loader2 className="h-7 w-7 animate-spin" aria-hidden="true" />
            )}
            <span>{effectiveStatusLabel}</span>
            {isCurrentStateProcessing && !isExpired && etaText && (
              <span className="text-white/70">{etaText}</span>
            )}
          </div>
        )}

        {showReadyChip && (
          <span
            data-testid="clip-card-status"
            className="pointer-events-none absolute bottom-2 left-2 z-10 rounded-full border border-white/15 bg-black/65 px-2 py-0.5 text-[11px] font-medium text-white backdrop-blur-sm"
          >
            {effectiveStatusLabel}
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-1 p-2.5">
        <div className="flex min-w-0 items-start gap-1.5">
          <h3 className="line-clamp-2 min-h-[2.5rem] flex-1 text-sm font-semibold leading-5" title={title}>
            {title}
          </h3>
          <ClipVersionBadge
            versionGroup={versionGroup}
            currentClipId={currentClip.id}
            currentVersionNumber={(currentClip as VersionBadgeClip).version_number}
          />
        </div>
        <p className="text-xs text-muted-foreground">{formatDateTime(clip.createdAt)}</p>

        <div className="mt-auto flex items-center gap-1.5 pt-2">
          <Button
            type="button"
            size="sm"
            variant={isDownloading ? 'secondary' : 'gradient'}
            data-testid="clip-card-download"
            className="relative h-9 min-w-0 flex-1 gap-1.5 overflow-hidden px-2 hover:scale-100"
            onClick={handleDownload}
            disabled={!effectiveDownloadUrl || isDownloading || isExpired}
            aria-label={isDownloading
              ? t('clipCard.downloadingProgress', { progress: downloadProgress })
              : t('clipCard.download')}
          >
            {isDownloading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            {isDownloading ? (
              <span>{downloadProgress}%</span>
            ) : (
              <span className={DOWNLOAD_LABEL_CLASS}>{t('clipCard.download')}</span>
            )}
            {isDownloading && (
              <span
                data-testid="clip-card-download-progress"
                className="absolute bottom-0 left-0 h-[3px] bg-gradient-primary transition-[width]"
                style={{ width: `${downloadProgress}%` }}
              />
            )}
          </Button>

          <ClipPostMenu
            post={post}
            className="shrink-0"
            triggerClassName="h-9 gap-1.5 px-2.5"
            labelClassName={SECONDARY_LABEL_CLASS}
            chevronClassName={SECONDARY_CHEVRON_CLASS}
          />
          {secondary === 'edit' && editUrl && (
            <Button asChild variant="secondary" size="sm" className="h-9 shrink-0 gap-1.5 px-2.5">
              <Link to={editUrl} data-testid="clip-card-edit" aria-label={t('clipCard.edit')}>
                <Edit className="h-4 w-4" />
                <span className={SECONDARY_LABEL_CLASS}>{t('clipCard.edit')}</span>
              </Link>
            </Button>
          )}

          {menuGroups.length > 0 && (
            <ClipCardMoreMenu
              groups={menuGroups}
              rating={clipRating}
              onAction={handleMenuAction}
              onRate={handleRating}
            />
          )}
        </div>
      </div>

      {/* Video Preview Modal: 9:16 unless the poster is horizontal */}
      <Dialog open={videoPreviewOpen} onOpenChange={setVideoPreviewOpen}>
        <DialogContent
          className={previewOrientation === 'landscape' ? 'max-w-4xl w-full' : 'w-auto max-w-fit'}
        >
          <DialogHeader>
            <DialogTitle className="line-clamp-2 pr-6">{currentClip.name || t('clipCard.videoPreview')}</DialogTitle>
          </DialogHeader>
          {effectiveDownloadUrl && (
            <div
              data-testid="clip-card-preview-frame"
              data-orientation={previewOrientation}
              className={cn(
                "relative overflow-hidden rounded-md bg-black",
                previewOrientation === 'landscape' ? "aspect-video w-full" : "mx-auto aspect-[9/16]",
              )}
              style={previewOrientation === 'portrait'
                ? { width: 'min(calc(100vw - 5rem), calc(75vh * 9 / 16), 405px)' }
                : undefined}
            >
              <video
                data-testid="clip-card-preview-video"
                src={effectiveDownloadUrl}
                poster={posterUrl ?? undefined}
                controls
                autoPlay
                playsInline
                className="h-full w-full object-contain"
              >
                {t('clipCard.browserNotSupportVideo')}
              </video>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Warning Modal */}
      <Dialog open={warningModalOpen} onOpenChange={setWarningModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('clipCard.videoNotEdited')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t('clipCard.videoNotEditedDesc')}
            </p>
            <p className="text-sm text-muted-foreground">
              {t('clipCard.addSubtitlesDesc')}
            </p>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setWarningModalOpen(false)}
              >
                {t('common.cancel')}
              </Button>
              {editUrl && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setWarningModalOpen(false);
                    navigate(editUrl);
                  }}
                >
                  <Edit className="w-4 h-4 mr-2" />
                  {t('clipCard.editVideoButton')}
                </Button>
              )}
              <Button
                onClick={() => {
                  setWarningModalOpen(false);
                  performDownload();
                }}
              >
                {t('clipCard.downloadAnyway')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Download Error Modal (popup blocked fallback) */}
      <Dialog open={downloadErrorOpen} onOpenChange={setDownloadErrorOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('clipCard.downloadFailedTitle')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t('clipCard.downloadFailedDesc')}
            </p>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setDownloadErrorOpen(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={() => {
                  setDownloadErrorOpen(false);
                  performDownload();
                }}
              >
                {t('common.retry')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ClipTitlesDialog
        open={titlesDialogOpen}
        onOpenChange={setTitlesDialogOpen}
        clipId={clip.id}
        clipName={currentClip.name || t('clipCard.noName')}
        existingCopy={freshClipData?.myClip?.copy || currentClip.copy}
      />

      {effectiveDownloadUrl && !isExpired && shareDialog}

      {coverDialogOpen && currentClip.finished_video_url && (
        <ClipCoverDialog
          key={currentClip.id}
          open={coverDialogOpen}
          onOpenChange={setCoverDialogOpen}
          clipId={currentClip.id}
          clipName={displayName ?? null}
          videoUrl={currentClip.finished_video_url}
          coverUrl={coverUrl}
          hasProAccess={hasProFeatures}
          onSaved={(url) => setCoverOverride({ url })}
        />
      )}
    </Card>
  );
};

export default ClipCard;
